import type { AxiosError, AxiosRequestConfig } from "axios";
import { axiosApiInstance } from "../axios";
import {
  QuickBooksInvoiceApiError,
  QuickBooksInvoiceCapabilities,
  QuickBooksInvoiceLatest,
  QuickBooksInvoiceRequest,
  QUICKBOOKS_INVOICE_STATUSES,
} from "../types/quickBooksInvoices";

const CAPABILITIES_PATH = "solmaz/quickbooks/invoice-requests/capabilities";
const REQUESTS_PATH = "solmaz/quickbooks/invoice-requests";

class InvoiceApiError extends Error implements QuickBooksInvoiceApiError {
  status?: number;
  code?: string;
  retryAfter?: number;
  isNetworkError: boolean;
  isCanceled: boolean;
  body?: { code?: string; message?: string; [key: string]: unknown };

  constructor(message: string, options: Partial<QuickBooksInvoiceApiError> = {}) {
    super(message);
    this.name = "QuickBooksInvoiceApiError";
    this.status = options.status;
    this.code = options.code;
    this.retryAfter = options.retryAfter;
    this.isNetworkError = options.isNetworkError ?? false;
    this.isCanceled = options.isCanceled ?? false;
    this.body = options.body;
  }
}

function errorFrom(error: unknown): InvoiceApiError {
  const candidate = error as { code?: string; name?: string; isAxiosError?: boolean };
  if (candidate?.code === "ERR_CANCELED" || candidate?.name === "CanceledError") {
    return new InvoiceApiError("Request canceled", { isCanceled: true });
  }
  if (!candidate?.isAxiosError && !(candidate && "response" in candidate)) {
    return new InvoiceApiError("Unable to reach QuickBooks invoice service", {
      isNetworkError: true,
    });
  }

  const axiosError = error as AxiosError;
  const body = (axiosError.response?.data || {}) as {
    code?: string;
    message?: string;
    [key: string]: unknown;
  };
  const retryAfterHeader = axiosError.response?.headers?.["retry-after"];
  const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined;
  return new InvoiceApiError(
    body.message || axiosError.message || "Unable to reach QuickBooks invoice service",
    {
      status: axiosError.response?.status,
      code: body.code,
      retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
      isNetworkError: !axiosError.response,
      body,
    },
  );
}

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await axiosApiInstance.request<T>({
      timeout: 15000,
      ...config,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
        "content-type": "application/json",
        ...config.headers,
      },
    });
    return response.data;
  } catch (error) {
    throw errorFrom(error);
  }
}

function validateStatus(value: QuickBooksInvoiceRequest, shipmentId?: number, requestId?: string): QuickBooksInvoiceRequest {
  if (!value || typeof value.request_id !== "string" || typeof value.shipment_id !== "number" ||
      !QUICKBOOKS_INVOICE_STATUSES.includes(value.status as typeof QUICKBOOKS_INVOICE_STATUSES[number]) ||
      typeof value.can_retry !== "boolean" ||
      (value.can_replace !== undefined && typeof value.can_replace !== "boolean") ||
      (value.generation !== undefined && typeof value.generation !== "number") ||
      (shipmentId !== undefined && value.shipment_id !== shipmentId) ||
      (requestId !== undefined && value.request_id !== requestId) ||
      (value.status === "succeeded" && (!value.solmaz_quickbooks_invoice_id || typeof value.solmaz_quickbooks_invoice_id !== "string"))) {
    throw new InvoiceApiError("QuickBooks returned an invalid invoice status", { code: "invalid_response" });
  }
  return value;
}

function validateCapabilities(value: QuickBooksInvoiceCapabilities): QuickBooksInvoiceCapabilities {
  if (!value || typeof value.enabled !== "boolean") {
    throw new InvoiceApiError("QuickBooks returned invalid capabilities", { code: "invalid_response" });
  }
  return value;
}

export const quickBooksInvoicesApi = {
  getCapabilities: (signal?: AbortSignal) =>
    request<QuickBooksInvoiceCapabilities>({
      method: "GET",
      url: CAPABILITIES_PATH,
      signal,
    }).then(validateCapabilities),

  enqueue: (shipmentId: number, signal?: AbortSignal) =>
    request<QuickBooksInvoiceRequest>({
      method: "POST",
      url: REQUESTS_PATH,
      data: { shipment_id: shipmentId },
      signal,
    }).then((value) => validateStatus(value, shipmentId)),

  getStatus: (requestId: string, signal?: AbortSignal) =>
    request<QuickBooksInvoiceRequest>({
      method: "GET",
      url: `${REQUESTS_PATH}/${encodeURIComponent(requestId)}`,
      signal,
    }).then((value) => validateStatus(value, undefined, requestId)),

  getLatestForShipment: (shipmentId: number, signal?: AbortSignal) =>
    request<QuickBooksInvoiceLatest>({
      method: "GET",
      url: `solmaz/quickbooks/shipments/${encodeURIComponent(shipmentId)}/invoice-request`,
      signal,
    }).then((value) => value === null ? null : validateStatus(value, shipmentId)),

  retry: (requestId: string, signal?: AbortSignal) =>
    request<QuickBooksInvoiceRequest>({
      method: "POST",
      url: `${REQUESTS_PATH}/${encodeURIComponent(requestId)}/retry`,
      signal,
    }).then((value) => validateStatus(value, undefined, requestId)),

  replace: (requestId: string, signal?: AbortSignal) =>
    request<QuickBooksInvoiceRequest>({
      method: "POST",
      url: `${REQUESTS_PATH}/${encodeURIComponent(requestId)}/replace`,
      signal,
    }).then((value) => validateStatus(value)),
};

export { InvoiceApiError };
export default quickBooksInvoicesApi;
